import React from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { inquiryTypeMeta } from './publicInquiryMeta'

function ArrowRightIcon({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className={className}>
      <path d="M4.5 10h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M11.5 6l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * 공개 문의 폼 (POST /public/inquiries/landing)
 * @param {{ scrollIntoViewOnSuccessId?: string | null, organizationMode?: boolean }} props
 */
export default function PublicLandingInquiryForm({ scrollIntoViewOnSuccessId = null, organizationMode = false }) {
  const [inquiryForm, setInquiryForm] = React.useState({
    inquiryType: 'TEAM',
    name: '',
    companyName: '',
    phone: '',
    email: '',
    message: '',
  })
  const [privacyAccepted, setPrivacyAccepted] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [submitOk, setSubmitOk] = React.useState(false)
  const [submitError, setSubmitError] = React.useState('')
  const activeInquiryMeta = inquiryTypeMeta[inquiryForm.inquiryType] || inquiryTypeMeta.TEAM

  const onChangeInquiryForm = (name, value) => {
    setInquiryForm((prev) => ({ ...prev, [name]: value }))
  }

  const onSubmitInquiry = async (e) => {
    e.preventDefault()
    setSubmitError('')
    setSubmitOk(false)
    if (!privacyAccepted) {
      setSubmitError('개인정보 수집 및 이용 동의가 필요합니다.')
      return
    }
    try {
      setSubmitting(true)
      await api.post(
        '/public/inquiries/landing',
        {
          inquiryType: inquiryForm.inquiryType,
          name: inquiryForm.name.trim(),
          companyName: inquiryForm.companyName.trim() || null,
          phone: inquiryForm.phone.trim(),
          email: inquiryForm.email.trim() || null,
          message: inquiryForm.message.trim(),
        },
        { __silent: true }
      )
      setSubmitOk(true)
      setInquiryForm({
        inquiryType: 'TEAM',
        name: '',
        companyName: '',
        phone: '',
        email: '',
        message: '',
      })
      setPrivacyAccepted(false)
      if (scrollIntoViewOnSuccessId) {
        try {
          window?.setTimeout?.(() => {
            document.getElementById(scrollIntoViewOnSuccessId)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
          }, 50)
        } catch {
          // ignore
        }
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err?.message || '문의 접수에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div id="public-landing-inquiry-form">
      <h3 className="text-2xl font-black sm:text-3xl">{organizationMode ? '도입 상담 신청' : '문의 남기기'}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        {organizationMode
          ? '아직 정해진 내용이 많지 않아도 괜찮아요. 아는 범위에서 적어 주세요.'
          : '개인이든 단체든 모두 접수할 수 있어요. 짧게만 적어 주셔도 괜찮아요.'}
      </p>
      <div className="mt-4 rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm leading-6 text-slate-700">
        <span className="font-semibold text-brand-navy">{organizationMode ? '조직 도입 상담' : activeInquiryMeta.label}</span>
        <span className="ml-2">
          {organizationMode
            ? '사용할 인원과 궁금한 점을 적어 주시면 알맞은 이용 방법을 안내해 드려요.'
            : activeInquiryMeta.helper}
        </span>
      </div>
      <form onSubmit={onSubmitInquiry} className="mt-8 space-y-5">
        <div className="grid gap-5 md:grid-cols-2">
          {organizationMode ? null : (
            <label className="block">
              <div className="mb-2 text-sm font-semibold text-slate-700">문의 유형</div>
              <select
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm shadow-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-sky-200"
                value={inquiryForm.inquiryType}
                onChange={(e) => onChangeInquiryForm('inquiryType', e.target.value)}
              >
                <option value="TEAM">{inquiryTypeMeta.TEAM.label}</option>
                <option value="PERSONAL">{inquiryTypeMeta.PERSONAL.label}</option>
              </select>
            </label>
          )}
          <label className="block">
            <div className="mb-2 text-sm font-semibold text-slate-700">이름</div>
            <input
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm shadow-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-sky-200"
              value={inquiryForm.name}
              onChange={(e) => onChangeInquiryForm('name', e.target.value)}
              placeholder="성함을 입력해 주세요"
              required
              aria-label="이름"
            />
          </label>
          <label className="block">
            <div className="mb-2 text-sm font-semibold text-slate-700">회사명 / 소속</div>
            <input
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm shadow-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-sky-200"
              value={inquiryForm.companyName}
              onChange={(e) => onChangeInquiryForm('companyName', e.target.value)}
              placeholder={organizationMode ? '회사명 또는 조직명을 입력해 주세요' : activeInquiryMeta.companyPlaceholder}
            />
            <div className="mt-2 text-xs text-slate-500">
              {organizationMode
                ? '보험대리점(GA)이나 법인·지점 이름을 적어 주세요.'
                : '단체로 문의하실 땐 법인·GA 이름을 적어 주시면 좋아요.'}
            </div>
          </label>
          <label className="block">
            <div className="mb-2 text-sm font-semibold text-slate-700">연락처</div>
            <input
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm shadow-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-sky-200"
              value={inquiryForm.phone}
              onChange={(e) => onChangeInquiryForm('phone', e.target.value)}
              placeholder="010-0000-0000"
              required
              aria-label="연락처"
            />
          </label>
          <label className="block">
            <div className="mb-2 text-sm font-semibold text-slate-700">이메일</div>
            <input
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm shadow-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-sky-200"
              value={inquiryForm.email}
              onChange={(e) => onChangeInquiryForm('email', e.target.value)}
              placeholder="답변 받을 이메일 주소"
              type="email"
              aria-label="이메일"
            />
          </label>
        </div>

        <label className="block">
          <div className="mb-2 text-sm font-semibold text-slate-700">문의 내용</div>
          <textarea
            className="min-h-[160px] w-full rounded-xl border border-slate-300 px-4 py-3 text-sm shadow-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-sky-200"
            value={inquiryForm.message}
            onChange={(e) => onChangeInquiryForm('message', e.target.value)}
            placeholder={
              organizationMode
                ? '예: 20명 정도 함께 사용하려고 합니다. 비용과 시작 방법이 궁금해요.'
                : activeInquiryMeta.messagePlaceholder
            }
            required
            aria-label="문의 내용"
          />
        </label>

        <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-600">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-navy focus:ring-brand-sky"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
          />
          <span>
            도입 문의 접수를 위한 개인정보 수집 및 이용에 동의합니다.
            <Link className="ml-1 font-semibold text-brand-navy underline-offset-4 hover:underline" to="/privacy">
              개인정보처리방침
            </Link>
          </span>
        </label>

        {submitError ? <div className="text-sm font-medium text-red-600">{submitError}</div> : null}
        {submitOk ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            문의가 접수되었습니다. 운영자가 확인 후 고객문의 답변 또는 별도 연락으로 안내드릴 예정입니다.
          </div>
        ) : null}

        <button
          type="submit"
          className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-navy py-3.5 text-sm font-bold text-white shadow-md transition-all duration-200 hover:bg-sky-700 disabled:opacity-60 motion-safe:hover:scale-[1.02] motion-safe:active:scale-[0.98] sm:w-auto sm:px-10"
          disabled={submitting}
        >
          {submitting ? '접수 중...' : organizationMode ? '도입 상담 신청하기' : '문의 남기기'}
          <ArrowRightIcon className="motion-safe:transition-transform motion-safe:duration-200 group-hover:translate-x-0.5" />
        </button>
      </form>
    </div>
  )
}
